import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { closeReportModal } from '../redux/slices/modalSlice';
import { reportPost } from '../redux/slices/postSlice';
import { IconX, IconAlertTriangle, IconCheck, IconLoader2 } from '@tabler/icons-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import './ReportModal.css';

const CATEGORIES = [
  { value: 'spam', label: 'Spam' },
  { value: 'harassment', label: 'Harassment' },
  { value: 'misinformation', label: 'Misinformation' },
  { value: 'illegal', label: 'Illegal or dangerous' },
  { value: 'other', label: 'Other' },
];

const ReportStatus = {
  IDLE: 'idle',
  SUBMITTING: 'submitting',
  SUCCESS: 'success',
  ERROR: 'error'
};

export default function ReportModal() {
  const dispatch = useDispatch();
  const { isOpen, itemId, itemType, postAuthorId } = useSelector(state => state.modal.reportModal);
  const currentUser = useSelector(state => state.auth.currentUser);
  const [category, setCategory] = useState('spam');
  const [details, setDetails] = useState('');
  const [status, setStatus] = useState(ReportStatus.IDLE);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setCategory('spam');
    setDetails('');
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setCategory('spam');
        setDetails('');
        setStatus(ReportStatus.IDLE);
        setError('');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Scroll lock + ESC close
  useEffect(() => {
    if (!isOpen) return;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') dispatch(closeReportModal());
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [isOpen, dispatch]);

  if (!isOpen) return null;
  if (typeof window === 'undefined') return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      dispatch(closeReportModal());
    }
  };

  const handleSubmit = async () => {
    if (!itemId || !currentUser?.uid) {
      setError('Please log in to report content');
      return;
    }

    if (!category) {
      setError('Please select a reason for reporting');
      return;
    }

    try {
      setStatus(ReportStatus.SUBMITTING);
      setError('');

      await dispatch(reportPost({
        postId: itemId,
        itemType: itemType || 'post',
        reasonCategory: category,
        reasonText: category === 'other' ? details.trim() : '',
        userId: currentUser.uid,
        postAuthorId: postAuthorId || null,
      })).unwrap();

      setStatus(ReportStatus.SUCCESS);

      toast.success('Report submitted successfully', {
        position: 'bottom-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      setTimeout(() => {
        dispatch(closeReportModal());
      }, 1500);
    } catch (err) {
      console.error('Error reporting content:', err);
      setStatus(ReportStatus.ERROR);

      const message = typeof err === 'string' ? err : (err?.message || '');
      const code = typeof err === 'object' ? err?.code : undefined;

      if (message.toLowerCase().includes('already reported')) {
        setError('You have already reported this content');
      } else if (code === 'permission-denied' || message.toLowerCase().includes('permission')) {
        setError('You do not have permission to report content');
      } else {
        setError(message || 'Failed to submit report. Please try again.');
      }

      toast.error(message || 'Failed to submit report', {
        position: 'bottom-center',
        autoClose: 3000,
        hideProgressBar: true,
      });
    }
  };

  const renderSuccessContent = () => (
    <div className="report-modal__success">
      <div className="report-modal__success-icon">
        <IconCheck size={32} />
      </div>
      <h3 className="report-modal__success-title">Report Submitted</h3>
      <p className="report-modal__success-text">
        Thank you for your report. Our team will review this content.
      </p>
    </div>
  );

  const renderFormContent = () => (
    <>
      <div className="report-modal__header">
        <h3 className="report-modal__title">Report Content</h3>
        <button
          onClick={() => dispatch(closeReportModal())}
          className="report-modal__close-btn"
          disabled={status === ReportStatus.SUBMITTING}
          aria-label="Close"
        >
          <IconX size={20} />
        </button>
      </div>

      <div className="report-modal__body">
        <div className="report-modal__field">
          <label className="report-modal__label">
            Reason for reporting <span className="report-modal__required">*</span>
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="report-modal__select"
            disabled={status === ReportStatus.SUBMITTING}
          >
            {CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        {category === 'other' && (
          <div className="report-modal__field">
            <label className="report-modal__label">
              Please provide details <span className="report-modal__required">*</span>
            </label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="Please explain the issue..."
              className="report-modal__textarea"
              disabled={status === ReportStatus.SUBMITTING}
            />
          </div>
        )}

        {error && (
          <div className="report-modal__error">
            <IconAlertTriangle size={18} />
            <span>{error}</span>
          </div>
        )}

        <button
          onClick={handleSubmit}
          disabled={status === ReportStatus.SUBMITTING || (category === 'other' && !details.trim())}
          className="report-modal__submit-btn"
        >
          {status === ReportStatus.SUBMITTING ? (
            <>
              <IconLoader2 className="report-modal__spinner" size={18} />
              Submitting...
            </>
          ) : (
            'Submit Report'
          )}
        </button>
      </div>

      <div className="report-modal__footer">
        Your report is anonymous. We take all reports seriously.
      </div>
    </>
  );

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="report-modal__backdrop"
          onClick={handleBackdropClick}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className="report-modal__content"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            onClick={(e) => e.stopPropagation()}
          >
            {status === ReportStatus.SUCCESS ? renderSuccessContent() : renderFormContent()}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
